import { createTheme } from '@mui/material/styles';

// =============================================================================
// Integraal Bank – Modern Swiss Digital Bank
// Deep navy with cyan accents, glassmorphic cards, Outfit typography
// =============================================================================

const currentTheme = createTheme({
  palette: {
    mode: 'dark',
    logo: 'dark',
    bank: {
      name: 'Integraal Bank',
      logo: '/integraal-logo.png',
    },
    primary: {
      main: '#19d0ef',
      dark: '#0595d3',
      light: '#5ee0f5',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#0082ba',
      dark: '#000060',
      contrastText: '#ffffff',
    },
    background: {
      default: '#0a0a2e',
      paper: 'rgba(255, 255, 255, 0.06)',
      surface: '#000040',
      gradient: 'radial-gradient(ellipse at 20% 0%, #0d1b4a 0%, #0a0a2e 50%, #060620 100%)',
    },
    text: {
      primary: '#f0f4f8',
      secondary: 'rgba(255, 255, 255, 0.6)',
      disabled: '#555580',
      hint: '#8888aa',
      dark: '#000000',
      body: '#4a4a4a',
      heading: '#2a2a2a',
    },
    success: {
      main: '#34d399',
      light: '#d4edda',
      dark: '#155724',
      border: '#c3e6cb',
    },
    error: {
      main: '#ef4444',
      light: '#fdecea',
    },
    warning: {
      main: '#f59e0b',
    },
    grey: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
    divider: 'rgba(255, 255, 255, 0.08)',
    action: {
      hover: 'rgba(25, 208, 239, 0.08)',
    },
    brand: {
      cyan: '#19d0ef',
      cyanHover: '#0595d3',
      cyanLight: '#5ee0f5',
      navy: '#0a0a2e',
      navyLight: '#0d1050',
      navyMid: '#0d1b4a',
      dialogBg: 'rgba(15, 15, 50, 0.95)',
    },
    border: {
      light: 'rgba(255, 255, 255, 0.08)',
      medium: 'rgba(255, 255, 255, 0.12)',
      dark: 'rgba(255, 255, 255, 0.16)',
      input: 'rgba(255, 255, 255, 0.15)',
      muted: 'rgba(255, 255, 255, 0.06)',
      tableCell: 'rgba(255, 255, 255, 0.08)',
      onDark: 'rgba(255, 255, 255, 0.06)',
      onDarkAlt: 'rgba(255, 255, 255, 0.08)',
    },
    voice: {
      initBg1: '#e0f7fa',
      initBg2: '#b2ebf2',
      initBg3: '#80deea',
      thinkBg1: '#fff8e1',
      thinkBg2: '#ffecb3',
      thinkBg3: '#ffe082',
      speakBg1: '#e8f5e9',
      speakBg2: '#c8e6c9',
      speakBg3: '#a5d6a7',
      overlayBg: 'linear-gradient(180deg, #0a0a2e 0%, #0d1b4a 50%, #0a0a2e 100%)',
    },
    gradient: {
      avatar: 'linear-gradient(135deg, #19d0ef 0%, #0078a8 100%)',
      voiceButton: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)',
      voiceTooltip: 'linear-gradient(135deg, #19d0ef 0%, #00bcd4 100%)',
    },
  },

  typography: {
    fontFamily: "'Inter', sans-serif",
    h1: {
      fontSize: '48px',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '22px',
      fontWeight: 300,
      letterSpacing: '0.01em',
    },
    h3: {
      fontSize: '20px',
      fontWeight: 600,
    },
    h4: {
      fontSize: '18px',
      fontWeight: 600,
    },
    h5: {
      fontSize: '16px',
      fontWeight: 600,
    },
    h6: {
      fontSize: '14px',
      fontWeight: 600,
    },
    body1: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '13px',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '12px',
      fontWeight: 400,
    },
    overline: {
      fontSize: '11px',
      fontWeight: 500,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
    button: {
      fontSize: '14px',
      fontWeight: 500,
      textTransform: 'none',
    },
  },

  shape: {
    borderRadius: 12,
    radiusSmall: 6,
    radiusMedium: 8,
    radiusLarge: 14,
    radiusXl: 16,
    radiusXxl: 20,
    radiusPill: 24,
  },

  shadows: [
    'none',
    '0 1px 4px rgba(0, 0, 0, 0.3)',                            // 1
    '0 4px 16px rgba(0, 0, 0, 0.4)',                            // 2
    '0 2px 8px rgba(25, 208, 239, 0.1)',                        // 3
    '0 8px 32px rgba(0, 0, 0, 0.5)',                            // 4
    '0 4px 20px rgba(25, 208, 239, 0.15)',                      // 5
    '0 2px 10px rgba(0, 0, 0, 0.2)',                            // 6
    '0 4px 20px rgba(0, 0, 0, 0.4)',                              // 7
    '0 6px 25px rgba(0, 0, 0, 0.5)',                              // 8
    '0 0 60px rgba(25, 208, 239, 0.3)',                         // 9
    '0 0 100px rgba(25, 208, 239, 0.2)',                        // 10
    'inset 0 0 30px rgba(255, 255, 255, 0.5)',                  // 11
    ...Array(13).fill('none'),
  ],

  spacing: 8,

  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 768,
      lg: 1068,
      xl: 1536,
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          margin: 0,
          fontFamily: "'Inter', sans-serif",
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontFamily: "'Inter', sans-serif",
          fontWeight: 500,
        },
        containedPrimary: {
          backgroundColor: '#19d0ef',
          color: '#0a0a2e',
          '&:hover': {
            backgroundColor: '#0595d3',
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.04)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '12px !important',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.06)',
            borderColor: 'rgba(25, 208, 239, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          },
          '&.Mui-expanded': {
            background: 'rgba(255, 255, 255, 0.06)',
            borderColor: 'rgba(25, 208, 239, 0.15)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            marginBottom: '16px',
          },
          '&:before': { display: 'none' },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          background: '#0d1050',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          background: '#0d1050',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: '#0d1050',
          backgroundImage: 'none',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: { fontFamily: "'Inter', sans-serif" },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#19d0ef',
          height: 2,
          borderRadius: 1,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', sans-serif",
          fontWeight: 500,
          letterSpacing: '0.02em',
          '&.Mui-selected': {
            color: '#19d0ef',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          // Ease border colour changes so hover/focus fade in smoothly
          // instead of snapping instantly.
          '& .MuiOutlinedInput-notchedOutline': {
            transition: 'border-color 0.25s ease',
          },
          // Subtle, on-brand hover tint instead of a hard white border.
          '&:hover:not(.Mui-focused):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(25, 208, 239, 0.35)',
          },
        },
      },
    },
  },
});

export default currentTheme;
